import { z } from 'zod';

import { defineQuery } from '@nesso/common-contracts/define';
import { practiceIdSchema } from '@nesso/common-contracts/ids';
import { metadata } from '@nesso/common-contracts/schema-metadata';

import { CONTACT_RELATIONSHIPS } from './client.commands';
import { clientIdSchema, personIdSchema } from './ids';

export const GET_CLIENT_BY_ID_TYPE = 'client/get-by-id';
export const GET_CLIENT_CONTACT_CANDIDATES_TYPE = 'client/get-contact-candidates';
export const GET_CLIENT_CONTACTS_TYPE = 'client/get-contacts';
export const GET_CLIENT_COUNT_TYPE = 'client/get-count';
export const GET_CLIENT_LIST_TYPE = 'client/get-list';
export const GET_CLIENT_PERSON_TYPE = 'client/get-person';
export const GET_CLIENT_PROFILE_TYPE = 'client/get-profile';
export const GET_CLIENT_RELATIONSHIPS_TYPE = 'client/get-relationships';
export const GET_CONTACT_PROFILE_TYPE = 'client/get-contact-profile';

// null when the person has no primary address, which is not the same as one left blank
const primaryAddressSchema = z
    .object({
        street1: z.string(),
        street2: z.string(),
        city: z.string(),
        state: z.string(),
        zip: z.string(),
    })
    .nullable();

// The client names and the notes are PHI; the ids and the relationship are not.
const contactClientLinkSchema = z.object({
    clientId: clientIdSchema.meta(metadata.nonSensitive),
    clientName: z.string(),
    relationship: z.enum(CONTACT_RELATIONSHIPS).meta(metadata.nonSensitive),
    isEmergencyContact: z.boolean(),
    isBillingContact: z.boolean(),
    notes: z.string().nullable(),
});

export const getClientById = defineQuery(
    GET_CLIENT_BY_ID_TYPE,
    z.object({
        id: clientIdSchema,
        practiceId: practiceIdSchema,
    }),
    z.object({
        id: z.uuid(),
        name: z.string(),
        consent: z.boolean(),
    }),
);

export const getClientContacts = defineQuery(
    GET_CLIENT_CONTACTS_TYPE,
    z.object({
        id: clientIdSchema.meta(metadata.nonSensitive),
        practiceId: practiceIdSchema.meta(metadata.nonSensitive),
    }),
    z.object({
        items: z.array(
            z.object({
                personId: z.uuid(),
                firstName: z.string(),
                lastName: z.string(),
                email: z.string().nullable(),
                phone: z.string().nullable(),
                relationship: z.enum(CONTACT_RELATIONSHIPS),
                isEmergencyContact: z.boolean(),
                isBillingContact: z.boolean(),
                notes: z.string().nullable(),
                // the client this person is the participant of, so the row can open that chart
                client: z
                    .object({
                        id: clientIdSchema.meta(metadata.nonSensitive),
                        name: z.string(),
                    })
                    .nullable(),
            }),
        ),
    }),
);

export const getClientRelationships = defineQuery(
    GET_CLIENT_RELATIONSHIPS_TYPE,
    z.object({
        id: clientIdSchema.meta(metadata.nonSensitive),
        practiceId: practiceIdSchema.meta(metadata.nonSensitive),
    }),
    z.object({
        items: z.array(contactClientLinkSchema),
    }),
);

export const getClientCount = defineQuery(
    GET_CLIENT_COUNT_TYPE,
    z.object({
        practiceId: practiceIdSchema,
    }),
    z.object({
        count: z.number(),
    }),
);

export const clientListItemSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    acuity: z.string().nullable(),
    consent: z.boolean(),
    pronouns: z.string().nullable(),
    treatmentApproach: z.string().nullable(),
    treatmentFocus: z.string().nullable(),
    // Query outputs carry ISO strings because that is what survives HTTP; the reader serialises.
    lastSession: z.iso.datetime().nullable(),
});

export type ClientListItem = z.infer<typeof clientListItemSchema>;

export const getClientList = defineQuery(
    GET_CLIENT_LIST_TYPE,
    z.object({
        practiceId: practiceIdSchema,
        search: z.string().trim().optional(),
        pageSize: z.number().optional(),
        cursor: z.string().nullable().optional(),
    }),
    z.object({
        items: z.array(clientListItemSchema),
        nextCursor: z.string().nullable(),
    }),
);

export const getClientProfile = defineQuery(
    GET_CLIENT_PROFILE_TYPE,
    z.object({
        id: clientIdSchema.meta(metadata.nonSensitive),
        practiceId: practiceIdSchema.meta(metadata.nonSensitive),
    }),
    z.object({
        id: clientIdSchema,

        firstName: z.string(),
        middleName: z.string().nullable(),
        lastName: z.string(),
        preferredFirstName: z.string().nullable(),
        // Query outputs carry ISO strings because that is what survives HTTP; the reader serialises.
        dateOfBirth: z.iso.datetime().nullable(),

        emailPrimary: z.string().nullable(),
        phonePrimary: z.string().nullable(),
        timeZone: z.string().nullable(),

        address: primaryAddressSchema,

        okToEmail: z.boolean().nullable(),
        okToCallText: z.boolean().nullable(),
        emailReminders: z.boolean(),
        textReminders: z.boolean(),

        sex: z.enum(['male', 'female', 'other']).nullable(),
        pronouns: z.string().nullable(),
    }),
);

// `search` is a fragment of somebody's name, so it is deliberately not `nonSensitive`.
export const getClientContactCandidates = defineQuery(
    GET_CLIENT_CONTACT_CANDIDATES_TYPE,
    z.object({
        id: clientIdSchema.meta(metadata.nonSensitive),
        practiceId: practiceIdSchema.meta(metadata.nonSensitive),
        search: z.string().trim().min(1),
        pageSize: z.number().optional().meta(metadata.nonSensitive),
    }),
    z.object({
        items: z.array(
            z.object({
                personId: z.uuid(),
                firstName: z.string(),
                lastName: z.string(),
                alreadyContact: z.boolean(),
            }),
        ),
    }),
);

export const getClientPerson = defineQuery(
    GET_CLIENT_PERSON_TYPE,
    z.object({
        id: personIdSchema.meta(metadata.nonSensitive),
        practiceId: practiceIdSchema.meta(metadata.nonSensitive),
    }),
    z.object({
        id: z.uuid(),
        firstName: z.string(),
        lastName: z.string(),
        emailPrimary: z.string().nullable(),
        phonePrimary: z.string().nullable(),
        address: primaryAddressSchema,
    }),
);

export const getContactProfile = defineQuery(
    GET_CONTACT_PROFILE_TYPE,
    z.object({
        id: personIdSchema.meta(metadata.nonSensitive),
        practiceId: practiceIdSchema.meta(metadata.nonSensitive),
    }),
    z.object({
        personId: personIdSchema.meta(metadata.nonSensitive),

        firstName: z.string(),
        lastName: z.string(),
        preferredFirstName: z.string().nullable(),
        pronouns: z.string().nullable(),

        emailPrimary: z.string().nullable(),
        phonePrimary: z.string().nullable(),
        timeZone: z.string().nullable(),
        address: primaryAddressSchema,

        okToEmail: z.boolean().nullable().meta(metadata.nonSensitive),
        okToCallText: z.boolean().nullable().meta(metadata.nonSensitive),
        emailReminders: z.boolean().meta(metadata.nonSensitive),
        textReminders: z.boolean().meta(metadata.nonSensitive),

        clients: z.array(contactClientLinkSchema),
    }),
);
